// SPDX-License-Identifier: AGPL-3.0-or-later
import {ASSET_FORMAT_POLICY, type AssetExtension, type AssetKind} from '@app/vendor/constants/AssetFormatPolicy';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export {getAcceptString} from '@app/vendor/constants/AssetFormatPolicy';

const ANIMATED_EXTENSIONS: ReadonlySet<AssetExtension> = new Set(['gif', 'apng']);
export function getAcceptStringFiltered(kind: AssetKind, animatedAllowed: boolean): string {
	const policy = ASSET_FORMAT_POLICY[kind];
	const exts = policy.upload.filter((ext) => animatedAllowed || !ANIMATED_EXTENSIONS.has(ext));
	const tokens: Array<string> = [];
	for (const ext of exts) tokens.push(`.${ext}`);
	for (const ext of exts) tokens.push(policy.mimes[ext]);
	return tokens.join(',');
}
export type AssetCopyErrorReason = 'unsupported_mime' | 'too_large' | 'bad_dimensions' | 'animated_requires_premium';
const EXTENSION_DISPLAY: Readonly<Record<AssetExtension, string>> = {
	png: 'PNG',
	jpeg: 'JPEG',
	webp: 'WebP',
	gif: 'GIF',
	apng: 'APNG',
	avif: 'AVIF',
	heic: 'HEIC',
	heif: 'HEIF',
	jxl: 'JXL',
};
function formatExtensions(kind: AssetKind): string {
	return ASSET_FORMAT_POLICY[kind].upload.map((ext) => EXTENSION_DISPLAY[ext]).join(', ');
}
function formatBytes(bytes: number): string {
	if (bytes >= 1024 * 1024) {
		const mb = bytes / (1024 * 1024);
		const rounded = Math.round(mb * 10) / 10;
		return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} MB`;
	}
	const kb = Math.round(bytes / 1024);
	return `${kb} KB`;
}
export function getSupportedFormatsLabel(i18n: I18n, kind: AssetKind): string {
	const formats = formatExtensions(kind);
	const maxSize = formatBytes(ASSET_FORMAT_POLICY[kind].maxBytes);
	return i18n._(msg`Supported: ${formats} · max ${maxSize}`);
}
export function getAssetFormatErrorMessage(i18n: I18n, kind: AssetKind, reason: AssetCopyErrorReason): string {
	const policy = ASSET_FORMAT_POLICY[kind];
	switch (reason) {
		case 'unsupported_mime': {
			const formats = formatExtensions(kind);
			return i18n._(msg`Unsupported file type. Allowed formats: ${formats}.`);
		}
		case 'too_large': {
			const maxSize = formatBytes(policy.maxBytes);
			return i18n._(msg`File is too large. Maximum size is ${maxSize}.`);
		}
		case 'bad_dimensions': {
			if (policy.dims == null) {
				return i18n._(msg`Image dimensions are out of range.`);
			}
			const min = policy.dims.min;
			const max = policy.dims.max;
			return i18n._(msg`Image dimensions must be between ${min}x${min} and ${max}x${max} pixels.`);
		}
		case 'animated_requires_premium': {
			return i18n._(msg`Animated uploads require a premium subscription.`);
		}
	}
}
